import React, { useState, useMemo } from 'react';
import { Building, Users, Tag, Calendar, Filter, ChevronDown, X, Grid3x3, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@clareefai/ui';
import { useGetProspectsQuery } from '../../store/prospectApi';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { ProspectResponse } from '../../store/prospectApi';

// Map backend status to frontend status
const mapStatusToKanbanColumn = (status: string): string => {
  const statusMap: Record<string, string> = {
    'NEW': 'introduce',
    'CONTACTED': 'request_sent',
    'QUALIFIED': 'scheduled',
    'PROPOSAL': 'scheduled',
    'NEGOTIATION': 'scheduled',
    'WON': 'completed',
    'LOST': 'failed',
  };
  return statusMap[status] || 'introduce';
};

interface ProspectCard {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  assignedPromoters?: string[];
  status: string;
  createdDate: string;
  sectors?: string[];
  companySize?: string | null;
}

const ProspectsList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: prospectsData, isLoading, error } = useGetProspectsQuery();
  const { data: promotersData } = useGetPromotersQuery();
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Convert backend data to frontend format
  const prospects: ProspectCard[] = useMemo(() => {
    if (!prospectsData) return [];
    return prospectsData.map((prospect) => ({
      id: prospect.id || '',
      name: `${prospect.firstName} ${prospect.lastName}`,
      email: prospect.email,
      phone: prospect.phone || '',
      company: prospect.company || '',
      assignedPromoters: prospect.assignedPromoters,
      status: mapStatusToKanbanColumn(prospect.status),
      createdDate: prospect.createdAt || new Date().toISOString(),
      sectors: prospect.sectors,
      companySize: prospect.companySize,
    }));
  }, [prospectsData]);

  const promoters = useMemo(() => {
    if (!promotersData) return [];
    return promotersData.map((promoter) => ({
      id: promoter.id,
      name: `${promoter.firstName} ${promoter.lastName}`,
      email: promoter.email,
      phone: promoter.phone || '',
      company: promoter.company || '',
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(promoter.firstName + ' ' + promoter.lastName)}&background=4339A0&color=fff`,
      status: 'available' as const,
      prospectsReferred: 0,
      conversionRate: 0,
      totalRewards: 0,
      joinedDate: promoter.createdAt,
    }));
  }, [promotersData]);

  // Filter states
  const [filters, setFilters] = useState({
    companySize: '',
    sectors: [] as string[],
    company: '',
    status: ''
  });

  const getStatusBadge = (status: string) => {
    const badges = {
      introduce: 'status-invited',
      request_sent: 'status-review',
      scheduled: 'status-available',
      completed: 'status-completed',
      failed: 'status-failed'
    };

    const labels = {
      introduce: t('prospects.kanbanCard.toIntroduce'),
      request_sent: t('prospects.kanbanCard.requestSent'),
      scheduled: t('prospects.kanbanCard.scheduled'),
      completed: t('prospects.kanbanCard.completed'),
      failed: t('prospects.kanbanCard.failed')
    };

    return (
      <span className={`status-badge ${badges[status as keyof typeof badges]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const handleProspectClick = (prospectId: string) => {
    navigate(`/prospect/${prospectId}`);
  };

  // Filter options
  const companySizeOptions = [
    t('prospects.list.sizes.xs'),
    t('prospects.list.sizes.s'),
    t('prospects.list.sizes.m'),
    t('prospects.list.sizes.l'),
    t('prospects.list.sizes.xl'),
    t('prospects.list.sizes.xxl'),
  ];

  const sectorOptions = [
    'Technology', 'Finance', 'Healthcare', 'Education', 'Commerce', 'Industry',
    'Services', 'Marketing', 'Consulting', 'Innovation', 'Startup', 'E-commerce',
    'Transport', 'Logistics', 'Manufacturing', 'Retail', 'Energy', 'Environment',
    'Media', 'Digital', 'Business', 'SaaS', 'Biotechnology', 'Automotive',
    'Fashion', 'Cybersecurity', 'Blockchain', 'Artificial Intelligence', 'Robotics',
    'CRM', 'Sales', 'Cloud', 'Enterprise', 'Database', 'Design', 'Creative',
    'Inbound', 'Support', 'Customer Service', 'Communication', 'Collaboration',
    'Project Management', 'Development', 'API', 'AI', 'Analytics', 'Research',
    'Laboratory', 'Renewable', 'Protection', 'FoodTech', 'PropTech', 'Aerospace',
    'Nanotechnology', 'Infrastructure', 'DevOps', 'Data', 'Gaming', 'Entertainment',
    'Sports', 'Luxury', 'Real Estate', 'Construction', 'Travel', 'Tourism',
    'Agriculture', 'Tools', 'Software', 'Automation'
  ];

  const statusOptions = [
    { value: 'introduce', label: t('prospects.kanbanCard.toIntroduce') },
    { value: 'request_sent', label: t('prospects.kanbanCard.requestSent') },
    { value: 'scheduled', label: t('prospects.kanbanCard.scheduled') },
    { value: 'completed', label: t('prospects.kanbanCard.completed') },
    { value: 'failed', label: t('prospects.kanbanCard.failed') }
  ];

  // Get unique companies
  const companyOptions = useMemo(() => {
    return [...new Set(prospects.map(p => p.company).filter(Boolean))].sort();
  }, [prospects]);

  // Get associated promoters for a prospect
  const getAssociatedPromoters = (prospect: ProspectCard) => {
    if (!prospect.assignedPromoters || prospect.assignedPromoters.length === 0) return [];
    return promoters.filter(p => 
      prospect.assignedPromoters?.includes(p.id)
    );
  };

  // Filter prospects
  const filteredProspects = useMemo(() => {
    return prospects.filter(prospect => {
      const prospectSectors = prospect.sectors || [];
      const prospectCompanySize = prospect.companySize;

      // Filter by company size
      if (filters.companySize && prospectCompanySize !== filters.companySize) {
        return false;
      }

      // Filter by sectors (at least one sector must match)
      if (filters.sectors.length > 0) {
        const hasMatchingSector = filters.sectors.some(sector => 
          prospectSectors.includes(sector)
        );
        if (!hasMatchingSector) {
          return false;
        }
      }

      // Filter by company
      if (filters.company && prospect.company !== filters.company) {
        return false;
      }

      // Filter by status
      if (filters.status && prospect.status !== filters.status) {
        return false;
      }

      return true;
    });
  }, [prospects, filters, promoters]);

  // Filter management
  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSectorToggle = (sector: string) => {
    setFilters(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }));
  };

  const clearFilters = () => {
    setFilters({
      companySize: '',
      sectors: [],
      company: '',
      status: ''
    });
  };

  const hasActiveFilters = filters.companySize || filters.sectors.length > 0 || filters.company || filters.status;

  return (
    <>
      <div className="p-6">
        {/* Filters section */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Filter className="w-5 h-5 text-gray-500" />
                <h3 className="text-lg font-semibold text-gray-900">{t('common.filters')}</h3>
                {hasActiveFilters && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                    {t('promoters.list.activeFiltersCount', {
                      count: [
                        filters.companySize ? 1 : 0,
                        filters.sectors.length,
                        filters.company ? 1 : 0,
                        filters.status ? 1 : 0
                      ].reduce((a, b) => a + b, 0)
                    })}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
                  >
                    <X className="w-4 h-4" />
                    <span>{t('common.clear')}</span>
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                >
                  <span className="text-sm font-medium">
                    {showFilters ? t('promoters.list.hideFilters') : t('promoters.list.showFilters')}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="p-4 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                {/* Filter by company size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('promoters.list.companySize')}
                  </label>
                  <select
                    value={filters.companySize}
                    onChange={(e) => handleFilterChange('companySize', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">{t('promoters.list.allSizes')}</option>
                    {companySizeOptions.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                {/* Filter by company */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.company')}
                  </label>
                  <select
                    value={filters.company}
                    onChange={(e) => handleFilterChange('company', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">{t('promoters.list.allCompanies')}</option>
                    {companyOptions.map(company => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                  </select>
                </div>

                {/* Filter by status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.status')}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">{t('promoters.list.allStatuses')}</option>
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                {/* Results counter */}
                <div className="flex items-end">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 w-full">
                    <div className="text-sm text-gray-600">{t('common.results')}</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {filteredProspects.length}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('prospects.list.ofProspects', { total: prospects.length })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Filter by sectors */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('promoters.list.industrySectorsSelected', { count: filters.sectors.length })}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {sectorOptions.map(sector => (
                    <button
                      key={sector}
                      onClick={() => handleSectorToggle(sector)}
                      className={`p-2 text-xs rounded-lg border transition-all duration-200 ${
                        filters.sectors.includes(sector)
                          ? 'bg-blue-100 border-blue-300 text-blue-800 font-medium'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                      }`}
                    >
                      {sector}
                    </button>
                  ))}
                </div>
                {filters.sectors.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {filters.sectors.map(sector => (
                      <span
                        key={sector}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {sector}
                        <button
                          onClick={() => handleSectorToggle(sector)}
                          className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* View mode toggle */}
        <div className="flex justify-end mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-[#4339A0] text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3x3 className="w-4 h-4" />
              <span className="text-sm font-medium">{t('promoters.list.gridView')}</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-[#4339A0] text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="text-sm font-medium">{t('promoters.list.tableView')}</span>
            </button>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="text-gray-500">{t('prospects.kanbanCard.loading')}</div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="text-center py-12">
            <div className="text-red-500">{t('prospects.kanbanCard.errorLoading')}</div>
          </div>
        )}

        {/* Results */}
        {!isLoading && !error && filteredProspects.length === 0 ? (
          <div className="text-center py-12">
            <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('prospects.list.noProspectsFound')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('prospects.list.noProspectsMatch')}
            </p>
            <button
              onClick={clearFilters}
              className="btn-primary"
            >
              {t('promoters.list.clearFilters')}
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {filteredProspects.map((prospect) => {
              const sectors = prospect.sectors || [];
              const companySize = prospect.companySize || 'N/A';
              const associatedPromoters = getAssociatedPromoters(prospect);

              return (
                <div
                  key={prospect.id}
                  className="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-all duration-200 cursor-pointer hover:bg-gray-100 border border-gray-200"
                  onClick={() => handleProspectClick(prospect.id)}
                >
                  {/* Header with name and company */}
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{prospect.name}</h3>
                    <p className="text-xs text-gray-600 truncate flex items-center">
                      <Building className="w-3 h-3 mr-1 flex-shrink-0" />
                      {prospect.company}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="mb-3 text-center">
                    {getStatusBadge(prospect.status)}
                  </div>

                  {/* Company size */}
                  <div className="mb-3">
                    <div className="flex items-center text-xs text-gray-600">
                      <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{companySize}</span>
                    </div>
                  </div>

                  {/* Industry sectors */}
                  <div className="mb-3">
                    <div className="flex items-start text-xs text-gray-600">
                      <Tag className="w-3 h-3 mr-1 flex-shrink-0 mt-0.5" />
                      <div className="flex flex-wrap gap-1 min-w-0">
                        {sectors.slice(0, 2).map((sector, index) => (
                          <span
                            key={index}
                            className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium truncate"
                          >
                            {sector}
                          </span>
                        ))}
                        {sectors.length > 2 && (
                          <span className="text-xs text-gray-500">+{sectors.length - 2}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Associated promoters - Avatars */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 font-medium">{t('nav.promoters')}:</span>
                      <span className="text-xs text-gray-500">{associatedPromoters.length}</span>
                    </div>
                    <div className="flex items-center mt-1">
                      <div className="flex -space-x-1">
                        {associatedPromoters.slice(0, 3).map((promoter) => (
                          <Avatar
                            key={promoter.id}
                            src={promoter.avatar}
                            name={promoter.name}
                            size="xs"
                            className="border-2 border-white"
                          />
                        ))}
                        {associatedPromoters.length > 3 && (
                          <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center">
                            <span className="text-xs text-gray-600 font-medium">
                              +{associatedPromoters.length - 3}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>


                  {/* Creation date */}
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-center text-xs text-gray-500">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>{new Date(prospect.createdDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                    </div>
                  </div>

                  {/* Notes (if present) */}
                  {prospect.notes && (
                    <div className="mt-3 p-2 bg-white rounded border-l-2 border-blue-400">
                      <p className="text-xs text-gray-700 line-clamp-2">{prospect.notes}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('prospects.list.columns.prospect')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.company')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('promoters.list.columns.size')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('promoters.list.columns.sectors')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('nav.promoters')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('prospects.list.columns.created')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProspects.map((prospect) => {
                    const sectors = prospect.sectors || [];
                    const companySize = prospect.companySize || 'N/A';
                    const associatedPromoters = getAssociatedPromoters(prospect);

                    return (
                      <tr
                        key={prospect.id}
                        onClick={() => handleProspectClick(prospect.id)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {prospect.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {prospect.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Building className="w-4 h-4 mr-2 text-gray-400" />
                            {prospect.company}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(prospect.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="w-4 h-4 mr-2 text-gray-400" />
                            {companySize}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {sectors.slice(0, 3).map((sector, index) => (
                              <span
                                key={index}
                                className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                              >
                                {sector}
                              </span>
                            ))}
                            {sectors.length > 3 && (
                              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                +{sectors.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex -space-x-1">
                              {associatedPromoters.slice(0, 3).map((promoter) => (
                                <Avatar
                                  key={promoter.id}
                                  src={promoter.avatar}
                                  name={promoter.name}
                                  size="sm"
                                  className="border-2 border-white"
                                />
                              ))}
                              {associatedPromoters.length > 3 && (
                                <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center">
                                  <span className="text-xs text-gray-600 font-medium">
                                    +{associatedPromoters.length - 3}
                                  </span>
                                </div>
                              )}
                            </div>
                            {associatedPromoters.length === 0 && (
                              <span className="text-sm text-gray-400">{t('common.none')}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            {new Date(prospect.createdDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProspectsList;